import { fileToBase64 } from './fileToBase64.ts';

// Anthropic vision caps images at 8000px/side and works best around a ~1568px
// long edge. We downscale oversized images in the browser BEFORE upload so
// extraction never fails on a big drop (and payloads stay small/fast). Non-images
// and already-small images pass through untouched (lossless).
const MAX_SIDE = 1568;

// Canvas can only RE-ENCODE to these; anything else (gif, bmp, …) becomes PNG.
// CRITICAL: the returned filename's extension MUST match the encoded bytes, else
// the committed file's media type lies and the vision API rejects it.
function reencodeTarget(type: string): { mime: string; ext: string } {
  if (type === 'image/png') return { mime: 'image/png', ext: 'png' };
  if (type === 'image/webp') return { mime: 'image/webp', ext: 'webp' };
  if (type === 'image/jpeg') return { mime: 'image/jpeg', ext: 'jpg' };
  return { mime: 'image/png', ext: 'png' }; // gif/bmp/svg/unknown → PNG
}

function withExt(name: string, ext: string): string {
  const base = name.split('/').pop() ?? name;
  const dot = base.lastIndexOf('.');
  return `${dot > 0 ? base.slice(0, dot) : base}.${ext}`;
}

/**
 * Prepare an image File for an upload endpoint: base64 payload + a filename whose
 * extension matches the bytes. Downscales only when a side exceeds MAX_SIDE;
 * otherwise returns the original bytes + name unchanged (lossless).
 */
export async function prepareImageUpload(
  file: File,
  maxSide = MAX_SIDE,
): Promise<{ base64: string; filename: string }> {
  if (!file.type.startsWith('image/')) {
    return { base64: await fileToBase64(file), filename: file.name };
  }

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { base64: await fileToBase64(file), filename: file.name }; // let the server validate
  }

  const longest = Math.max(bitmap.width, bitmap.height);
  if (longest <= maxSide) {
    bitmap.close();
    return { base64: await fileToBase64(file), filename: file.name }; // within bounds — keep original
  }

  const scale = maxSide / longest;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return { base64: await fileToBase64(file), filename: file.name };
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const { mime, ext } = reencodeTarget(file.type);
  const base64 = canvas.toDataURL(mime, 0.92).split(',')[1] ?? '';
  return { base64, filename: withExt(file.name, ext) };
}
